import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Input } from 'src/@/components/ui/input';
import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import widgetCmsService from 'src/services/widgetCmsService';
import GenericTable from '../../../../components/table/GenericTable';
import { WIDGET_CMS_DESIGN as D } from './WidgetCms.design';

export default function WidgetCmsPage({ onOpenProjeto }) {
    const { t } = useTranslation('settings');
    const { cliente } = useAuth();
    const clienteId = cliente?._id;

    const HEAD_LABELS = [
        { id: 'nome', label: t('widgetCms.list.table.headProject', 'Projeto'), className: 'flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider' },
        { id: 'canais', label: t('widgetCms.list.table.headChannels', 'Canais'), className: 'w-48 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:block' },
        { id: 'news', label: t('widgetCms.list.table.headPosts', 'Posts'), className: 'w-24 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:block' },
        { id: 'artigos', label: t('widgetCms.list.table.headArticles', 'Artigos'), className: 'w-24 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:block' },
        { id: 'acoes', label: '', className: 'w-10' }
    ];

    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pesquisa, setPesquisa] = useState('');

    const carregar = useCallback(async () => {
        if (!clienteId) return;
        setLoading(true);
        try {
            const res = await widgetCmsService.listarProjetos(clienteId);
            setLista(res.data || []);
        } catch (e) {
            console.log('erro ao listar projetos de widget', e);
        } finally {
            setLoading(false);
        }
    }, [clienteId]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const listaFiltrada = lista.filter(p =>
        !pesquisa || p.nome?.toLowerCase().includes(pesquisa.toLowerCase())
    );

    const totalPosts = lista.reduce((acc, p) => acc + (p.newsCount || 0), 0);
    const totalArtigos = lista.reduce((acc, p) => acc + (p.artigosCount || 0), 0);
    const totalCanaisVinculados = lista.reduce((acc, p) => acc + (p.canais?.length || 0), 0);

    const stats = [
        { label: t('widgetCms.list.stats.projects', 'Projetos'), value: lista.length, icon: 'solar:widget-add-bold', bg: 'bg-violet-50', color: 'text-violet-600' },
        { label: t('widgetCms.list.stats.newsPosts', 'Posts de novidades'), value: totalPosts, icon: 'solar:document-text-bold', bg: 'bg-blue-50', color: 'text-blue-600' },
        { label: t('widgetCms.list.stats.helpArticles', 'Artigos de ajuda'), value: totalArtigos, icon: 'solar:book-bookmark-bold', bg: 'bg-green-50', color: 'text-green-600' },
        { label: t('widgetCms.list.stats.linkedChannels', 'Canais vinculados'), value: totalCanaisVinculados, icon: 'fluent:channel-share-28-filled', bg: 'bg-orange-50', color: 'text-orange-600' }
    ];

    return (
        <div className={D.page.container}>
            {/* Header */}
            <div className={D.page.header.container}>
                <div className="flex items-center gap-4">
                    <div className={D.page.header.iconBox}>
                        <Iconify icon="solar:widget-add-bold" width={28} className="text-[#7C3AED]" />
                    </div>
                    <div>
                        <h1 className={D.page.header.title}>{t('widgetCms.list.header.title', 'Central do Widget')}</h1>
                        <p className={D.page.header.subtitle}>
                            {t('widgetCms.list.header.subtitle', 'Projetos com novidades, central de ajuda e configurações do widget de suporte')}
                        </p>
                    </div>
                </div>
                <button className={D.page.actionButton} onClick={() => onOpenProjeto(null)}>
                    <Iconify icon="eva:plus-outline" width={16} />
                    {t('widgetCms.list.header.newProjectButton', 'Novo projeto')}
                </button>
            </div>

            {/* Stats */}
            <div className={D.stats.grid}>
                {stats.map(stat => (
                    <div key={stat.label} className={D.stats.card}>
                        <div className={`${D.stats.iconBox} ${stat.bg}`}>
                            <Iconify icon={stat.icon} width={20} className={stat.color} />
                        </div>
                        <div>
                            <p className={D.stats.value}>{stat.value}</p>
                            <p className={D.stats.label}>{stat.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filtro */}
            <div className={D.filters.grid}>
                <div>
                    <label className={D.filters.label}>{t('widgetCms.list.filters.searchLabel', 'Buscar')}</label>
                    <div className="relative">
                        <Iconify
                            icon="eva:search-outline"
                            width={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                            value={pesquisa}
                            onChange={e => setPesquisa(e.target.value)}
                            placeholder={t('widgetCms.list.filters.searchPlaceholder', 'Nome do projeto...')}
                            className={D.filters.searchInput}
                        />
                    </div>
                </div>
            </div>

            {/* Tabela */}
            {listaFiltrada.length === 0 && !loading ? (
                <div className={D.emptyState.container}>
                    <div className={D.emptyState.iconBox}>
                        <Iconify icon="solar:widget-add-bold" width={28} className="text-violet-300" />
                    </div>
                    <p className={D.emptyState.title}>
                        {pesquisa ? t('widgetCms.list.emptyState.titleSearch', 'Nenhum projeto encontrado') : t('widgetCms.list.emptyState.titleEmpty', 'Nenhum projeto de widget ainda')}
                    </p>
                    <p className={D.emptyState.subtitle}>
                        {t('widgetCms.list.emptyState.subtitle', 'Crie um projeto para publicar novidades e artigos de ajuda no seu widget')}
                    </p>
                    {!pesquisa && (
                        <span className={D.emptyState.link} onClick={() => onOpenProjeto(null)}>
                            {t('widgetCms.list.emptyState.link', 'Criar o primeiro projeto')}
                        </span>
                    )}
                </div>
            ) : (
                <GenericTable headLabels={HEAD_LABELS}>
                    {listaFiltrada.map(projeto => (
                        <div
                            key={projeto._id}
                            className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-50 last:border-0 hover:bg-violet-50/30 cursor-pointer transition-colors group"
                            onClick={() => onOpenProjeto(projeto)}
                        >
                            <div className="flex-1 flex items-center gap-3 min-w-0">
                                <div
                                    className="w-9 h-9 rounded-xl flex-shrink-0"
                                    style={{
                                        background: `linear-gradient(135deg, ${projeto.settings?.gradient?.from || '#7C3AED'}, ${projeto.settings?.gradient?.to || '#EC4899'})`
                                    }}
                                />
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{projeto.nome}</p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {projeto.settings?.saudacao || t('widgetCms.list.table.noGreeting', 'Sem saudação configurada')}
                                    </p>
                                </div>
                            </div>
                            <div className="w-48 hidden md:block">
                                {projeto.canais?.length ? (
                                    <div className="flex flex-wrap gap-1">
                                        {projeto.canais.map(c => (
                                            <span key={c._id} className="text-[11px] bg-gray-50 border border-gray-200 text-gray-600 rounded-full px-2 py-0.5">
                                                {c.nome}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-xs text-gray-400">{t('widgetCms.list.table.noChannel', 'Sem canal')}</span>
                                )}
                            </div>
                            <div className="w-24 hidden md:block text-sm text-gray-600">{projeto.newsCount || 0}</div>
                            <div className="w-24 hidden md:block text-sm text-gray-600">{projeto.artigosCount || 0}</div>
                            <div className="w-10 flex justify-end">
                                <Iconify icon="eva:chevron-right-outline" width={18} className="text-gray-300 group-hover:text-[#7C3AED]" />
                            </div>
                        </div>
                    ))}
                </GenericTable>
            )}
        </div>
    );
}

WidgetCmsPage.propTypes = {
    onOpenProjeto: PropTypes.func.isRequired
};
